import { useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { FirebaseError } from 'firebase/app';
import {
  collection, limit, onSnapshot, query, where,
} from 'firebase/firestore';
import { useEffect } from 'react';

import { db } from '../../config';
import { ClientRequestFirestoreResponse } from '../../types';

const COLLECTION_PATH = 'client-request';
const MAX_ITEMS = 50;

const toMillis = (request: ClientRequestFirestoreResponse): number => request.createdAt?.toMillis?.() ?? 0;

/**
 * Subskrypcja realtime na nowe zgłoszenia (status === 'new'). Używana przez
 * dzwoneczek powiadomień w topBarze. Filtruje tylko po `status` (pojedynczy
 * indeks automatyczny), a sortowanie po dacie odbywa się po stronie klienta,
 * żeby nie wymagać złożonego indeksu Firestore.
 */
export const useGetNewClientRequestsFromFirestore = (): UseQueryResult<
ClientRequestFirestoreResponse[],
FirebaseError
> => {
  const queryClient = useQueryClient();
  const queryKey = [COLLECTION_PATH, 'new'];

  useEffect(() => {
    const newRequestsQuery = query(
      collection(db, COLLECTION_PATH),
      where('status', '==', 'new'),
      limit(MAX_ITEMS),
    );

    const unsubscribe = onSnapshot(
      newRequestsQuery,
      (snapshot) => {
        const data = snapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }) as ClientRequestFirestoreResponse)
          .sort((a, b) => toMillis(b) - toMillis(a));
        queryClient.setQueryData<ClientRequestFirestoreResponse[]>(queryKey, data);
      },
      (error) => {
        // eslint-disable-next-line no-console
        console.error('Failed to subscribe to new client requests', error);
        queryClient.setQueryData<ClientRequestFirestoreResponse[]>(queryKey, []);
      },
    );

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient]);

  return useQuery<ClientRequestFirestoreResponse[], FirebaseError>({
    queryKey,
    queryFn: () => queryClient.getQueryData<ClientRequestFirestoreResponse[]>(queryKey) ?? [],
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};
